/**
 * Pulls invoice fields out of the text of a PDF.
 *
 * Every invoice layout is different, so this reads the labels Dutch, English
 * and German invoices actually use rather than assuming a position. Whatever
 * it cannot find is reported back as missing instead of being invented - the
 * converter previously filled the gaps with placeholders, which produced a
 * document that looked complete and contained nothing from the file.
 */

export interface ExtractedInvoice {
  invoiceNumber?: string;
  issueDate?: string;
  dueDate?: string;
  supplierName?: string;
  supplierVatNumber?: string;
  currency: string;
  taxExclusive?: number;
  taxAmount?: number;
  taxInclusive?: number;
  taxPercent?: number;
  missing: string[];
}

const LABELS = {
  invoiceNumber: /(?:factuur(?:nummer|nr\.?)|invoice\s*(?:number|no\.?|#)|rechnungsnummer)\s*[:#]?\s*([A-Za-z0-9][A-Za-z0-9\-_/]{1,24})/i,
  issueDate: /(?:factuurdatum|datum|invoice\s*date|date|rechnungsdatum)\s*[:#]?\s*([0-3]?\d[-/.][01]?\d[-/.]\d{2,4}|\d{4}-\d{2}-\d{2})/i,
  dueDate: /(?:vervaldatum|betaal(?:datum|termijn)|due\s*date|f(?:ä|ae)lligkeitsdatum)\s*[:#]?\s*([0-3]?\d[-/.][01]?\d[-/.]\d{2,4}|\d{4}-\d{2}-\d{2})/i,
  vatNumber: /\b((?:NL|BE|DE|FR|IT|ES|AT|PL)\s?[0-9A-Z]{8,13})\b/,
  taxExclusive: /(?:subtotaal|totaal\s*excl(?:usief)?(?:\s*btw)?|net\s*(?:amount|total)|subtotal|nettobetrag)\s*[:#]?\s*(?:€|eur)?\s*([\d.,]+)/i,
  taxAmount: /(?:btw(?:\s*bedrag)?|vat(?:\s*amount)?|mwst\.?|tax)\s*(?:\d{1,2}\s*%)?\s*[:#]?\s*(?:€|eur)?\s*([\d.,]+)/i,
  // \b matters: without it "Subtotaal" matches "totaal" and the net amount is
  // read as the gross total.
  taxInclusive: /(?:\btotaal\s*(?:incl(?:usief)?(?:\s*btw)?)?|\btotal\s*(?:amount)?|\bgesamtbetrag|te\s*betalen|amount\s*due)\s*[:#]?\s*(?:€|eur)?\s*([\d.,]+)/i,
  taxPercent: /\b(\d{1,2})\s*%/,
};

/** Dutch invoices write 1.234,56 where English ones write 1,234.56. */
export function parseAmount(raw: string): number | undefined {
  const cleaned = raw.trim().replace(/\s/g, '');
  if (!cleaned) return undefined;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalised: string;

  if (lastComma > lastDot) {
    normalised = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    normalised = cleaned.replace(/,/g, '');
  } else {
    normalised = cleaned.replace(/[.,]/g, '');
  }

  const value = Number.parseFloat(normalised);
  return Number.isFinite(value) ? value : undefined;
}

/** Returns YYYY-MM-DD, the only form UBL accepts. */
export function normaliseDate(raw: string): string | undefined {
  const trimmed = raw.trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const parts = trimmed.split(/[-/.]/).map((p) => p.trim());
  if (parts.length !== 3) return undefined;

  let [day, month, year] = parts;
  if (year.length === 2) year = `20${year}`;
  if (day.length === 4) [day, year] = [year, day]; // already year-first

  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (!d || !m || !y || m > 12 || d > 31) return undefined;

  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function firstMatch(text: string, pattern: RegExp): string | undefined {
  const match = text.match(pattern);
  return match?.[1]?.trim() || undefined;
}

/**
 * The supplier is rarely labelled, so fall back to the first line that looks
 * like a company rather than a heading or an amount.
 */
function guessSupplier(text: string): string | undefined {
  for (const line of text.split('\n').map((l) => l.trim()).slice(0, 15)) {
    if (line.length < 3 || line.length > 60) continue;
    if (/factuur|invoice|rechnung|datum|date|btw|vat|totaal|total/i.test(line)) continue;
    if (/^[\d\s.,€-]+$/.test(line)) continue;
    return line;
  }
  return undefined;
}

export function extractInvoice(text: string): ExtractedInvoice {
  const collapsed = text.replace(/[ \t]+/g, ' ');

  const invoiceNumber = firstMatch(collapsed, LABELS.invoiceNumber);
  const issueRaw = firstMatch(collapsed, LABELS.issueDate);
  const dueRaw = firstMatch(collapsed, LABELS.dueDate);
  const supplierName = guessSupplier(text);
  const supplierVatNumber = firstMatch(collapsed, LABELS.vatNumber);

  const taxExclusive = firstMatch(collapsed, LABELS.taxExclusive);
  const taxAmount = firstMatch(collapsed, LABELS.taxAmount);
  const taxInclusive = firstMatch(collapsed, LABELS.taxInclusive);
  const taxPercent = firstMatch(collapsed, LABELS.taxPercent);

  const result: ExtractedInvoice = {
    invoiceNumber,
    issueDate: issueRaw ? normaliseDate(issueRaw) : undefined,
    dueDate: dueRaw ? normaliseDate(dueRaw) : undefined,
    supplierName,
    supplierVatNumber: supplierVatNumber?.replace(/\s/g, ''),
    currency: /\bUSD|\$/.test(collapsed) ? 'USD' : 'EUR',
    taxExclusive: taxExclusive ? parseAmount(taxExclusive) : undefined,
    taxAmount: taxAmount ? parseAmount(taxAmount) : undefined,
    taxInclusive: taxInclusive ? parseAmount(taxInclusive) : undefined,
    taxPercent: taxPercent ? Number(taxPercent) : undefined,
  missing: [],
  };

  // Derive what is missing but computable, so a two-of-three invoice still
  // produces correct totals.
  if (result.taxInclusive === undefined && result.taxExclusive !== undefined && result.taxAmount !== undefined) {
    result.taxInclusive = Math.round((result.taxExclusive + result.taxAmount) * 100) / 100;
  }
  if (result.taxExclusive === undefined && result.taxInclusive !== undefined && result.taxAmount !== undefined) {
    result.taxExclusive = Math.round((result.taxInclusive - result.taxAmount) * 100) / 100;
  }
  if (result.taxAmount === undefined && result.taxInclusive !== undefined && result.taxExclusive !== undefined) {
    result.taxAmount = Math.round((result.taxInclusive - result.taxExclusive) * 100) / 100;
  }

  const required: Array<[keyof ExtractedInvoice, string]> = [
    ['invoiceNumber', 'invoiceNumber'],
    ['issueDate', 'issueDate'],
    ['supplierName', 'supplierName'],
    ['taxExclusive', 'taxExclusive'],
    ['taxAmount', 'taxAmount'],
    ['taxInclusive', 'taxInclusive'],
  ];
  result.missing = required.filter(([key]) => result[key] === undefined).map(([, label]) => label);

  return result;
}
